import { useMemo } from 'react';
import { FormatIcon } from '../components/FormatIcon.tsx';
import { Thumb } from '../components/Thumb.tsx';
import { deriveHome, formatNote, slateSummary } from '../lib/derive.js';
import { formatLabel } from '../lib/formats.js';
import { formatDate, formatGap } from '../lib/format.js';
import type { WatchEvent, WatchItem } from '../lib/model.js';
import { Chips } from './rows.tsx';

interface Props {
  items: readonly WatchItem[];
  events: readonly WatchEvent[];
  lastSeenAt: string | null;
  now: Date;
  onOpen: (item: WatchItem) => void;
  onAdd: () => void;
}

/**
 * Everything you are waiting on — the whole shape of the wait, which is the
 * part the home screen deliberately no longer shows.
 *
 * Two halves, and the second is the bigger one: things with a date, drawn on a
 * timeline with the quiet stretches left in, and then the pile with no date at
 * all. Undated is the normal state here, so it reads as restful rather than as
 * a list of things to fix.
 */
export function Slate({ items, events, lastSeenAt, now, onOpen, onAdd }: Props) {
  const model = useMemo(
    () => deriveHome({ items, events, lastSeenAt, now }),
    [items, events, lastSeenAt, now],
  );

  if (items.length === 0) {
    return (
      <div className="empty">
        <h2>Nothing on the slate yet.</h2>
        <p>Add a book, a film, a series, or the person who makes them.</p>
        <p>
          <button type="button" className="primary-btn" onClick={onAdd}>
            Add the first thing
          </button>
        </p>
      </div>
    );
  }

  const dated = model.spine.filter((r) => r.kind === 'entry').length;

  return (
    <>
      <div className="verdict">
        <h1>{slateSummary(model, now)}</h1>
        <span className="since">
          {dated} dated · {model.waiting.length} with no date
        </span>
      </div>

      {/* Side by side on a wide screen: the timeline keeps its single column,
          because a timeline read in two columns stops being one — but the pile
          with no dates has no order to preserve, so it can sit beside it and
          use the width. */}
      <div className="slatecols">
        {dated > 0 && (
        <div className="section">
          <div className="section-head">
            <span className="label">Coming</span>
            <span className="count">{dated}</span>
          </div>

          <ol className="spine">
            {model.spine.map((row, i) =>
              row.kind === 'gap' ? (
                <li key={`gap-${i}`} className="spine-gap">
                  <span>— {formatGap(row.months)} —</span>
                </li>
              ) : (
                <li
                  key={row.item.subscriptionId}
                  className={row.next ? 'spine-entry next' : 'spine-entry'}
                >
                  <button onClick={() => onOpen(row.item)}>
                    <Thumb
                      src={row.item.imageUrl}
                      kind={row.item.kind}
                      medium={row.item.medium}
                      size="md"
                    />
                    <span className="spine-body">
                      <span className="spine-date">
                        {formatDate(row.date)}
                        {/* Which form of it arrives on that date. A paperback
                            in March and a hardback in March are not the same
                            piece of news. */}
                        <span className="spine-slot">
                          <FormatIcon slot={row.slot} medium={row.item.medium} size={12} />
                          {formatLabel(row.slot, row.item.medium)}
                        </span>
                      </span>
                      <span className="spine-title">{row.item.title}</span>
                      {row.item.byline && <span className="byline">{row.item.byline}</span>}
                      <Chips item={row.item} />
                    </span>
                  </button>
                </li>
              ),
            )}
          </ol>
        </div>
        )}

        {model.waiting.length > 0 && (
        <div className="section waitsection">
          <div className="section-head">
            <span className="label">No date, no news</span>
            <span className="count">{model.waiting.length}</span>
          </div>

          <ul className="waiting">
            {model.waiting.map((row) => (
              <li
                key={row.item.subscriptionId}
                className={row.kind === 'placeholder' ? 'phantom' : undefined}
              >
                <button onClick={() => onOpen(row.item)}>
                  <Thumb
                    src={row.item.imageUrl}
                    kind={row.item.kind}
                    medium={row.item.medium}
                    size="sm"
                  />
                  <span className="waiting-body">
                    <span className="w-title">
                      {row.kind === 'placeholder' ? row.label : row.item.title}
                    </span>
                    <span className="w-by">
                      {row.kind === 'placeholder'
                        ? row.note
                        : waitingByline(row.item, row.staleness, now)}
                    </span>
                    <Chips item={row.item} />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        )}
      </div>

      <p className="attribution">
        This product uses the TMDb API but is not endorsed or certified by TMDb.
      </p>
    </>
  );
}

function waitingByline(item: WatchItem, staleness: string | null, now: Date): string {
  const role =
    item.kind === 'creator'
      ? `${item.medium === 'book' ? 'author' : 'director'} · anything new`
      : (item.byline ?? 'no date yet');

  // Said out loud, because this is the one line where the app is deliberately
  // withholding something: the hardback is out and you are not being told.
  return [role, formatNote(item, now), staleness].filter(Boolean).join(' · ');
}
